import { call, put, select, takeLatest } from "redux-saga/effects";
import { types } from "reducers/thirdPayment";
import {
    validateThirdPayment,
    loadListRequest,
    downloadTransactionLinestMiddleware,
    downloadTransactionFiletMiddleware,
    downloadListRequestMiddleware,
    downloadConfirmation,
    downloadDocumentMiddleware,
    validateDateMiddleware,
    recollectionJWT,
    payrollUploadDocument,
    payrollDeleteDocument,
    payrollSend,
    downloadPayrollTemplate,
    payrollListDownloads,
    payrollGetDownload,
} from "middleware/thirdPayment";
import { transactionStatus } from "pages/transactions/constants";
import { credentialsErrorMessage } from "util/validateMessage";

import * as i18n from "util/i18n";
import * as form from "middleware/form";
import { downloadPdf, downloadXls, downloadTransferTemplateCsvAsExcel, downloadEcheqTemplateCsvAsExcel } from "util/download";
import { actions as notificationActions } from "reducers/notification";
import { push } from "connected-react-router";

const sagas = [
    takeLatest(types.CREATE_THIRDPAYMENT_REQUEST, validateThirdPay),
    takeLatest(types.LOAD_LIST_REQUEST, loadListThirdRequest),
    takeLatest(types.LOAD_TRANSACTION_REQUEST, loadTransactionRequest),
    takeLatest(types.DOWNLOAD_TRANSACTION_LINE_REQUEST, downloadTransactionLines),
    takeLatest(types.DOWNLOAD_TRANSACTION_FILE_REQUEST, downloadFileTransactionRequest),
    takeLatest(types.DOWNLOAD_LIST_REQUEST, downloadListRequest),
    takeLatest(types.DOWNLOAD_CONFIRMATION_REQUEST, downloadConfirm),
    takeLatest(types.LOAD_MORE_LIST_REQUEST, loadMoreListThirdRequest),
    takeLatest(types.VALIDATE_DATE_REQUEST, validateDate),

    takeLatest(types.DOWNLOAD_DOCUMENT_REQUEST, downloadDocumentRequest),
    takeLatest(types.CANCEL_THIRDPAYMENT_REQUEST, cancelTransaction),
    takeLatest(types.PAYMENT_RECOLLECTION_REQUEST, getRecollectionJWT),

    takeLatest(types.PAYROLL_UPLOAD_DOCUMENT_REQUEST, payrollUploadDocumentRequest),
    takeLatest(types.PAYROLL_DELETE_DOC_REQUEST, payrollDeleteDocumentRequest),
    takeLatest(types.PAYROLL_SEND_REQUEST, payrollSendRequest),
    takeLatest(types.PAYROLL_DOWNLOAD_TEMPLATE_REQUEST, downloadPayrollTemplateRequest),
    takeLatest(types.PAYROLL_LIST_DOWNLOADS_REQUEST, listPayrollDownloads),
    takeLatest(types.PAYROLL_GET_DOWNLOAD_REQUEST, getPayrollDownload),
];

export default sagas;

const ticketStates = {
    COR020W: "ERROR",
    COR023I: transactionStatus.PENDING,
    COR000I: transactionStatus.FINISHED,
    COR013I: transactionStatus.SCHEDULED,
    COR060I: transactionStatus.PROCESSING,
    API515I: transactionStatus.PENDING_BACKOFFICE,
};

const statusValue = (state) => {
    switch (state) {
        case "TODOS":
            return "";
        case "PENDIENTE DE FIRMA":
            return "PENDING";
        case "FIRMA COMPLETA":
            return "PROCESSING";
        case "FIRMA RECHAZADA":
            return "CANCELLED";
        case "FIRMA PENDIENTE VENCIDA":
            return "RETURNED";
        case "ANULADO":
            return "REVOKED";
        case "FALLO":
            return "FAILED";
        case "ENVIADO AL BANCO":
            return "FINISHED";
        case "PROCESADO CONFORME":
            return "1";
        case "PROCESADO CON RECHAZOS":
            return "2";
        default:
            return "";
    }
};

function* validateThirdPay({ summary, formikBag, credentialType }) {
    try {
        const response = yield call(validateThirdPayment, summary);
        if (response.type === "I") {
            yield put({
                type: types.THIRDPAYMENT_TICKET_SUCCESS,
                thirdPartiesTicket: {
                    status: ticketStates[response.data.code],
                    ...response.data,
                },
            });
            yield put({
                type: types.SET_SUMMARY_THIRDPAYMENT,
                thirdPaymentData: {},
            });

            if (response.data?.data?.OUT_TIME) {
                yield put(
                    notificationActions.showNotification(
                        response.data?.data?.OUT_TIME || i18n.get("global.unexpectedError"),
                        "warning",
                        ["lotPaymentTransactionPage"],
                    ),
                );
                return;
            }

            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(i18n.get("transfer.success"), "success", [
                        "lotPaymentTransactionPage",
                    ]),
                );
            }
            if (ticketStates[response.data.code] === ticketStates.COR060I) {
                yield put(
                    notificationActions.showNotification(i18n.get("transfer.pending"), "success", [
                        "lotPaymentTransactionPage",
                    ]),
                );
            }
            return;
        }

        if (response.type === "W") {
            let message = credentialsErrorMessage(response.data?.data);
            if (response.data?.data?.password || response.data?.data?.otp) {
                message =
                    credentialType === "password"
                        ? i18n.get("preferences.password.password.incorrect")
                        : i18n.get("login.step1.secondFactor.invalid");

                formikBag.setErrors({
                    credential: message,
                });
            }

            yield put(
                notificationActions.showNotification(
                    message || response.data.message || i18n.get("global.unexpectedError"),
                    "error",
                    ["newLotPaymentSummaryPage", "lotPaymentTransactionPage"],
                ),
            );
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* loadListThirdRequest({ filters }) {
    const response = yield call(loadListRequest, {
        ...filters,
        status: filters.status === "TODOS" ? "" : statusValue(filters.status),
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["third"]));
        yield put({ type: types.LOAD_LIST_FAILURE });
    } else {
        const { latestTotalPages, latestPageNumber: newLatestMovementsPageNumber } = response.data.data;
        yield put({
            type: types.LOAD_LIST_SUCCESS,
            transactionLinesStatus: response.data.data.transactionLinesStatus,
            latestTotalPages,
            latestPageNumber: newLatestMovementsPageNumber,
        });
    }
}

function* loadMoreListThirdRequest({ filters }) {
    const response = yield call(loadListRequest, {
        ...filters,
        status: filters.status === "TODOS" ? "" : filters.status,
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["third"]));
        yield put({ type: types.LOAD_MORE_LIST_FAILURE });
    } else {
        const { latestTotalPages, latestPageNumber: newLatestMovementsPageNumber } = response.data.data;
        yield put({
            type: types.LOAD_MORE_LIST_SUCCESS,
            transactionLinesStatus: response.data.data.transactionLinesStatus,
            latestTotalPages,
            latestPageNumber: newLatestMovementsPageNumber,
        });
    }
}

function* loadTransactionRequest({ idTransaction }) {
    const response = yield call(form.readTransaction, idTransaction);
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["form"]));
        yield put({ type: types.LOAD_TRANSACTION_FAILURE });
    } else {
        const { data, idTransactionStatus } = response.data.data.transaction;
        yield put({
            type: types.LOAD_TRANSACTION_SUCCESS,
            data: { ...data, idTransactionStatus },
        });
    }
}
function* downloadTransactionLines({ format, transactionLines }) {
    const { data } = yield call(downloadTransactionLinestMiddleware, format, transactionLines);

    const { content, fileName } = data.data;

    downloadPdf(fileName, content);
}
function* downloadListRequest({ format, filterThirdParties }) {
    const params = {
        format,
        amountFrom: filterThirdParties.amountFrom,
        amountTo: filterThirdParties.amountTo,
        dateFrom: filterThirdParties.dateFrom,
        dateTo: filterThirdParties.dateTo,
        status: statusValue(filterThirdParties.status),
        typePayment: filterThirdParties.typePayment,
    };
    try {
        const { data } = yield call(downloadListRequestMiddleware, params);
        const { content, fileName } = data.data;
        if (format === "pdf") {
            downloadPdf(fileName, content);
        } else {
            downloadXls(fileName, content);
        }
        yield put({ type: types.DOWNLOAD_LIST_SUCCESS });
    } finally {
        yield put({ type: types.DOWNLOAD_LIST_FAILURE });
    }
}

function* downloadFileTransactionRequest({
    format,
    transactionLines,
    typePayment,
    fileNam,
    id,
    reference,
    dateProcess,
    transactionOrder,
    currency,
    idTransactionStatus,
    accountName,
    isDetailDownload,
}) {
    const { data, type } = yield call(
        downloadTransactionFiletMiddleware,
        format,
        transactionLines,
        typePayment,
        fileNam,
        id,
        reference,
        dateProcess,
        transactionOrder,
        currency,
        idTransactionStatus,
        accountName,
        isDetailDownload,
    );

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_TRANSACTION_FILE_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        const { content, fileName } = data.data;

        downloadPdf(fileName, content);

        yield put({ type: types.DOWNLOAD_TRANSACTION_FILE_SUCCESS });
    }
}

function* downloadConfirm({ summary }) {
    const { type, data } = yield call(downloadConfirmation, summary);

    if (type === "W") {
        yield put({ type: types.DOWNLOAD_CONFIRMATION_FAILURE });
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["form"]));
    } else {
        const { content, fileName } = data.data;

        downloadPdf(fileName, content);

        yield put({ type: types.DOWNLOAD_CONFIRMATION_SUCCESS });
    }
}

function* downloadDocumentRequest({ fileId }) {
    const { data, type } = yield call(downloadDocumentMiddleware, fileId);
    if (type === "W") {
        yield put({ type: types.DOWNLOAD_DOCUMENT_FAILURE });
    } else {
        const { content, fileName } = data.data;
        try {
            downloadTransferTemplateCsvAsExcel(fileName, content);
        } catch (error) {
            // error al descargar archivo
        }

        yield put({ type: types.DOWNLOAD_DOCUMENT_SUCCESS });
    }
}

function* cancelTransaction({ summary, formikBag, credentialType }) {
    const params = {
        [`_${credentialType}`]: summary[`_${credentialType}`],
    };

    try {
        const response = yield call(form.cancelTransaction, summary.idTransaction, { ...params });

        if (response.type === "W") {
            if (response.data.code === "COR020W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["newLotPaymentSummaryPage"]),
                );
                if (response.data?.data?.password) {
                    const msj =
                        credentialType === "password"
                            ? "preferences.password.password.incorrect"
                            : "login.step1.secondFactor.invalid";
                    yield put(
                        notificationActions.showNotification(i18n.get(msj), "error", ["newLotPaymentSummaryPage"]),
                    );
                    formikBag.setErrors({
                        credential: i18n.get(msj),
                    });
                }
            }
        } else {
            if (response.data.code === "API524W") {
                yield put(
                    notificationActions.showNotification(response.data?.message, "error", ["newLotPaymentSummaryPage"]),
                );
                return;
            }

            yield call(
                form.updateUserEditor,
                summary.idTransaction,
                summary.userId,
                "CANCELLED",
                null,
                summary.cancelTransaction,
            );

            yield put({
                type: types.THIRDPAYMENT_TICKET_SUCCESS,
                thirdPartiesTicket: {
                    status: "CANCELLED",
                    ...response.data,
                    idTransaction: summary.idTransaction,
                },
            });
            yield put({
                type: types.SET_SUMMARY_THIRDPAYMENT,
                thirdPaymentData: {},
            });
            if (ticketStates[response.data.code] === ticketStates.COR000I) {
                yield put(
                    notificationActions.showNotification(i18n.get("transfer.success"), "success", [
                        "lotPaymentTransactionPage",
                    ]),
                );
            }
        }
    } finally {
        formikBag.setSubmitting(false);
    }
}

function* validateDate({ dateProcess }) {
    const { data, type } = yield call(validateDateMiddleware, dateProcess);
    if (type === "W") {
        yield put({ type: types.VALIDATE_DATE_FAILURE });
    } else {
        const { dateWorkingDay } = data.data;
        const isValidDate = dateWorkingDay?.slice(0, 10) === dateProcess;
        yield put({ type: types.VALIDATE_DATE_SUCCESS, isValidDate });
    }
}

function* getRecollectionJWT() {
    const userId = yield select((state) => state.session.user.userId);
    try {
        const response = yield call(recollectionJWT, { userId });
        if (response.type !== "E") {
            yield put({
                type: types.PAYMENT_RECOLLECTION_SUCCESS,
                recolection: response.data.data,
            });
            return;
        }
        yield put({
            type: types.PAYMENT_RECOLLECTION_FAILURE,
            recolection: {
                error: response.data.message,
            },
        });
        yield put(notificationActions.showNotification(response.data.message, "error", ["recollections"]));
    } catch (error) {
        yield put({
            type: types.PAYMENT_RECOLLECTION_FAILURE,
            recolection: {
                error: error.data.message,
            },
        });
        yield put(notificationActions.showNotification(error.data.message, "error", ["recollections"]));
    }
}

function* payrollUploadDocumentRequest({ documentToUpload }) {
    const response = yield call(payrollUploadDocument, documentToUpload);

    if (response && response.status === 200) {

        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.data.errorMsg, "error", ["PayrollNotification"]));
            yield put({
                type: types.PAYROLL_UPLOAD_DOCUMENT_FAILURE,
                documentToUploadRequest: response?.data?.data?.errorMsg,
                documentCode: documentToUpload.uploaderRequestId,
            });
            yield put({
                type: types.PAYROLL_SET_UPLOAD_ERROR_REQUEST,
                externalUploadError: response?.data?.data?.errorMsg + "|" + Date.now(),
            });
        } else {
            yield put({
                type: types.PAYROLL_UPLOAD_DOCUMENT_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: documentToUpload.uploaderRequestId,
                fileId: response?.data?.data?.fileId,
            });
        }
    } else {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["PayrollNotification"]));
        yield put({ type: types.PAYROLL_UPLOAD_DOCUMENT_FAILURE });
    }
}

function* payrollDeleteDocumentRequest({ fileId }) {
    const params = {
        fileId,
    };
    const response = yield call(payrollDeleteDocument, params);

    if (response && response.status === 200) {

        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.data.errorMsg, "error", ["PayrollNotification"]));
            yield put({ type: types.PAYROLL_DELETE_DOC_FAILURE });
        } else {
            yield put({
                type: types.PAYROLL_DELETE_DOC_SUCCESS,
                documentToUploadRequest: response.data?.data,
                documentCode: '1',
            });

        }
    } else {
        yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["PayrollNotification"]));
        yield put({ type: types.PAYROLL_DELETE_DOC_FAILURE });
    }
}

function* payrollSendRequest({ params, formikBag }) {
    try {

        const response = yield call(payrollSend, params);
        if (response && response.status === 200) {

            if (response.type === "W") {
                if (response.data?.data?.otp) {
                    formikBag.setErrors({
                        credential: i18n.get("validate.secondFactor.invalid")
                    });
                    formikBag.setSubmitting(false);

                    yield put(notificationActions.showNotification(i18n.get("validate.secondFactor.invalid"), "error", ["PayrollConfirm"]));
                    yield put({
                        type: types.PAYROLL_SEND_FAILURE,
                    });

                    return;
                }

                yield put(notificationActions.showNotification(response.data.data.errorMsg, "error", ["PayrollConfirm"]));
                yield put({
                    type: types.PAYROLL_SEND_FAILURE,
                });

                formikBag.setSubmitting(false);
                return;
            }

            yield put({
                type: types.PAYROLL_SEND_SUCCESS,
            });

           yield put(push(`/massPayroll/transaction/${response.data.idTransaction}`,  { isConfirm: true }));

        } else {
            yield put({ type: types.PAYROLL_SEND_FAILURE });
            yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["PayrollConfirm"]));

            yield put(push(`/massPayroll/transaction/${response.data.idTransaction}`,{ isConfirm: true }));
        }

    } catch (error) {

        yield put({ type: types.PAYROLL_SEND_FAILURE });
        yield put(push(`/massPayroll/transaction/${error.data.idTransaction}`,{ isConfirm: true }));
    }

      yield put({
                type: types.PAYROLL_CLEAN_REQUEST,
        });

}

function* downloadPayrollTemplateRequest() {
    const response = yield call(downloadPayrollTemplate, {
        idTemplate: "TEMPLATE_PAYROLL",
        nameFile: "Sueldos_20250101_30999999999_123",
        format: "txt",
    });
    if (response.type === "W") {
        yield put(notificationActions.showNotification(response.data.message, "error", ["PayrollNotification"]));
    } else {
        yield put(notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["PayrollNotification"]));

        const { fileName, content } = response.data.data;
        try {
            downloadEcheqTemplateCsvAsExcel(fileName, content);
        } catch (error) {
            console.log(error);
            // error al descargar archivo
        }
    }
}

function* listPayrollDownloads({ params }) {
    try {
        const { cuit } = params;
        const response = yield call(payrollListDownloads,  { cuit });

        if (response.type === "W") {
            yield put(notificationActions.showNotification(response.data.message, "error", ["PayrollNotification"]));
        } else {
            const { downloadsList } = response.data.data;
            yield put({
                type: types.PAYROLL_LIST_DOWNLOADS_SUCCESS,
                payrollDownloadsList: downloadsList,
            });
        }
     } catch (error) {
           yield put(notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", ["PayrollNotification"]));
           yield put({
                type: types.PAYROLL_LIST_DOWNLOADS_FAILURE,
            });
    }
}

function* getPayrollDownload({ params }) {
    try {
        const { fileToDownload } = params;
        const response = yield call(payrollGetDownload, { fileToDownload: fileToDownload });

        if (response.type === "W") {
            yield put(notificationActions.showNotification(i18n.get("payroll.downloadFile.error"), "error", ["PayrollNotification"]));
            yield put({
                 type: types.PAYROLL_GET_DOWNLOAD_FAILURE,
            });
        } else {
            const { fileContent} = response.data.data;

            yield put(notificationActions.showNotification(i18n.get("global.successDownload"), "success", ["PayrollNotification"]));
            yield put({
                type: types.PAYROLL_GET_DOWNLOAD_SUCCESS,
            });
         
            try {
                downloadPdf(fileToDownload, fileContent);
            } catch (error) {
                console.log(error);
                // error al descargar archivo
            }
        }
     } catch (error) {
           yield put(notificationActions.showNotification(i18n.get("payroll.downloadFile.error"), "error", ["PayrollNotification"]));
           yield put({
                type: types.PAYROLL_GET_DOWNLOAD_FAILURE,
            });
    }
}
